import React, { useLayoutEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ActionSheet, useMqSelect } from '@fhs/ui';
import { IItem } from '@rbi-ctg/menu';
import { ScreenScrollView } from 'components/layout/screen-scroll-view';
import { ProductOverview } from 'components/product-detail/product-overview';
import { useScrollRestoration } from 'hooks/use-scroll-restoration';
import { useProductWizardContext } from 'state/product-wizard';
import { useProductWizardFlow } from 'state/product-wizard/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard/types';
import { useScrollContext } from 'state/scroll';
import { useFlag } from 'utils/launchdarkly';

import { NutritionInformation } from '../nutrition-information';
import { ProductHomeCta, ProductStaticMenuCta } from '../product-cta';
import { ProductCustomization } from '../product-customization';
import { ProductHome } from '../product-home';

import { ProductCustomizationContainer } from './product-customization-container';

export const ProductLayout: React.FC<React.PropsWithChildren<unknown>> = () => {
  const isMobile = useMqSelect({ $ltDesktop: true }, false);

  const { currentStep, slotKeyToCustomize } = useProductWizardFlow();
  const { userSelections, isSimplyOffer } = useProductWizardContext();
  const { scrollRef, scrollTo } = useScrollContext();
  const { contentOffset } = useScrollRestoration(String(currentStep));
  const useEnableShowPickerAspectsInStaticMenu = useFlag(
    'enable-show-picker-aspects-in-static-menu'
  );

  const itemToCustomize = useMemo<IItem | null>(
    () => userSelections.comboSlot?.get(slotKeyToCustomize)?.selectedItem || null,
    [slotKeyToCustomize, userSelections.comboSlot]
  );

  const showCustomization =
    !!itemToCustomize && currentStep === ProductWizardStep.ProductCustomization;

  useLayoutEffect(() => {
    if (contentOffset) {
      scrollTo({
        y: contentOffset?.y,
        animated: false,
      });
    }
  }, [contentOffset, scrollTo]);

  const ScrollComponent: any = isSimplyOffer ? ActionSheet.ScrollView : ScreenScrollView;

  const hideCta =
    isSimplyOffer && isMobile && currentStep === ProductWizardStep.ProductCustomization;

  return (
    <>
      {showCustomization ? (
        <SafeAreaView edges={['bottom']} style={[StyleSheet.absoluteFill, { zIndex: 1 }]}>
          <ProductCustomizationContainer>
            <ProductCustomization itemToCustomize={itemToCustomize} />
          </ProductCustomizationContainer>
        </SafeAreaView>
      ) : null}
      <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
        <ScrollComponent ref={scrollRef} flex={1} isSimplyOffer={isSimplyOffer}>
          <ProductOverview />
          {useEnableShowPickerAspectsInStaticMenu && (
            <ProductHome showStaticPickers={useEnableShowPickerAspectsInStaticMenu} />
          )}
          <NutritionInformation />
        </ScrollComponent>
        {!hideCta && (
          <>
            {useEnableShowPickerAspectsInStaticMenu ? (
              <ProductStaticMenuCta showStaticPickers={useEnableShowPickerAspectsInStaticMenu} />
            ) : (
              <ProductHomeCta />
            )}
          </>
        )}
      </SafeAreaView>
    </>
  );
};
